import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowUpRight, Crown, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { API } from "@/lib/site";

const INCLUDES = [
  "Unlimited Reading",
  "AI Explain Mode",
  "Premium Voices",
  "Reading History",
  "All Future Features",
  "Privacy Protection",
];

export default function Pricing() {
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again in a moment.");
      setLoading(false);
    }
  };

  return (
    <section id="pricing" className="relative bg-[#050F1F]/50 py-24 lg:py-32" aria-labelledby="pricing-heading">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <span id="pricing-heading" className="sr-only">Pricing</span>
        <SectionHeading
          align="center"
          eyebrow={{ index: "06", label: "Founder Plan" }}
          title={<>Independence for <span className="text-glow-gradient">less than a soda.</span></>}
          sub="Join as a Founder and lock in early pricing forever. No tiers. No upsells. Everything included."
        />

        <Reveal delay={0.15}>
          <div
            className="relative mx-auto mt-14 max-w-lg overflow-hidden rounded-[2rem] border border-[#00D4FF]/35 bg-gradient-to-b from-[#0C2340] to-[#071A35] p-8 shadow-[0_0_60px_rgba(0,212,255,0.15)] sm:p-10"
            data-testid="pricing-founder-card"
          >
            <div
              className="pointer-events-none absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-[#00D4FF]/20 blur-[80px]"
              aria-hidden="true"
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#00D4FF]/12 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#00D4FF]">
                <Crown className="h-3.5 w-3.5" aria-hidden="true" />
                Founder Plan
              </span>

              <div className="mt-7 flex items-end gap-2">
                <span className="font-display text-6xl font-black tracking-tight text-white" data-testid="pricing-price">
                  $2.99
                </span>
                <span className="pb-2 text-lg text-slate-400">/month</span>
              </div>
              <p className="mt-2 text-base font-medium text-slate-300">Less than a soda.</p>

              <ul className="mt-8 space-y-3.5" aria-label="Founder Plan includes">
                {INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-base text-slate-200">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00D4FF]/15">
                      <Check className="h-3.5 w-3.5 text-[#00D4FF]" strokeWidth={3} aria-hidden="true" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={startCheckout}
                disabled={loading}
                data-testid="pricing-become-founder-button"
                className="group mt-9 flex min-h-[56px] w-full items-center justify-center gap-3 rounded-full bg-[#2563EB] px-8 py-4 text-base font-bold text-white shadow-[0_0_30px_rgba(37,99,235,0.5)] transition-all duration-300 hover:scale-[1.02] hover:bg-[#1D4ED8] hover:shadow-[0_0_44px_rgba(0,212,255,0.55)] disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    Opening secure checkout…
                  </>
                ) : (
                  <>
                    Become a Founder
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-sm text-slate-400">
                Secure checkout by Stripe. Founder pricing locked in for life. Cancel anytime.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
